import { ApiError } from '@/api/client';
import { serverErrorsOf } from '@/lib/formErrors';
import { showErrorToast } from '@/lib/toasts';
import { credentialsMissingStatus } from './credentialModel';
import type { SubmitState } from './useSubmitState';

interface ConnectSubmit {
  /** The local validation failed: show the errors and send nothing. */
  invalid: boolean;
  /** Stores the token and creates the account / connection; `false` stops without closing (the form shows why). */
  run: () => Promise<void | false>;
  onDone: () => void;
  /** The field a 409 ("already exists") is shown under. */
  slotField: string;
  /** What to say when `/credentials` (or the resource) is missing on this API. */
  missing: string;
}

/** What a failed connect shows: the API's per-field errors, else a form problem; infrastructure failures also toast. */
export function reportConnectFailure(err: unknown, sub: Pick<SubmitState, 'setProblem' | 'setServer'>, slotField: string, missing: string): void {
  if (err instanceof ApiError && credentialsMissingStatus(err.status)) {
    sub.setProblem(new Error(missing));
    return;
  }
  const mapped = serverErrorsOf(err, slotField);
  sub.setServer(mapped.fields);
  if (Object.keys(mapped.fields).length === 0) sub.setProblem(mapped.form ? new Error(mapped.form) : err);
  if (mapped.toast) showErrorToast(mapped.toast);
}

/** The submit of a connect form (provider account, Bitbucket workspace): validate, run, close, or report the failure. */
export async function submitConnect(sub: SubmitState, { invalid, run, onDone, slotField, missing }: ConnectSubmit): Promise<void> {
  sub.setSubmitted(true);
  sub.setProblem(null);
  sub.setServer({});
  if (invalid) return;
  sub.setBusy(true);
  try {
    if ((await run()) === false) return;
    onDone();
  } catch (err) {
    reportConnectFailure(err, sub, slotField, missing);
  } finally {
    sub.setBusy(false);
  }
}
